import React, { useCallback, useEffect, useState } from 'react'

import { Box, Divider, Skeleton, TextField, Typography } from '@mui/material'

import Button from '../Button/Button.component'
import EmptyState from '../EmptyState/EmptyState.component'
import Stepper from '../Stepper/Stepper.component'

import { addItemTrackingNumber } from '../../../redux/states/offers/addItemTrackingNumber/addItemTrackingNumber.slice'
import { AddItemTrackingNumberRequestPayload } from '../../../redux/states/offers/addItemTrackingNumber/addItemTrackingNumber.types'
import { getItemRequestTrackingDetails } from '../../../redux/states/offers/getItemRequestTrackingDetails/getItemRequestTrackingDetails.slice'
import {
  GetItemRequestTrackingDetailsRequestPayload,
  GetItemRequestTrackingDetailsSuccessResponse,
  TrackingDetailsKeys
} from '../../../redux/states/offers/getItemRequestTrackingDetails/getItemRequestTrackingDetails.types'
import { updateItemRequestTrackingDetails } from '../../../redux/states/offers/updateItemRequestTrackingDetails/updateItemRequestTrackingDetails.slice'
import { UpdateItemRequestTrackingDetailsRequestPayload } from '../../../redux/states/offers/updateItemRequestTrackingDetails/updateItemRequestTrackingDetails.types'
import { RootState } from '../../../redux/store/store.types'
import status from '../../functions/Status/status.dictionaries'
import { UserRole } from '../../functions/UserRole/userRoleManagement.types'
import { getUserRole } from '../../functions/UserRole/userRoleManagment.functions'
import { useAppDispatch, useAppSelector } from '../../hooks/redux.hooks'
import useResponsiveness from '../../hooks/responsive.hooks'
import Modal from '../Modal/Modal.functions'
import { getCurrentTrackingDetailsStatus, getStatuses } from './TrackingDetails.functions'
import styles from './TrackingDetails.styles'
import { TrackingDetailsProps, TrackingDetailsStatus } from './TrackingDetails.types'

const TrackingDetails: React.FC<TrackingDetailsProps> = (props: TrackingDetailsProps) => {
  const { itemRequestId, isOpen, refreshMainList } = props

  const [visibleStatuses, setVisibleStatuses] = useState<TrackingDetailsStatus[]>([])
  const [currentStatus, setCurrentStatus] = useState<TrackingDetailsKeys | null>(null)
  const [currentStatusIndex, setCurrentStatusIndex] = useState<number>(-1)
  const [isLastStepCompleted, setIsLastStepCompleted] = useState<boolean>(false)
  const [isConfirmedDeliveredByShopper, setIsConfirmedDeliveredByShopper] = useState<boolean>(false)
  const [isConfirmedDeliveredByTraveler, setIsConfirmedDeliveredByTraveler] = useState<boolean>(false)

  const [trackingNumber, setTrackingNumber] = useState<string>('')

  // Local state for tracking details data
  const [trackingDetailsData, setTrackingDetailsData] = useState<GetItemRequestTrackingDetailsSuccessResponse | null>(
    null
  )
  const [trackingDetailsLoading, setTrackingDetailsLoading] = useState<boolean>(false)

  const { loading: addItemTrackingNumberLoading } = useAppSelector((state: RootState) => state.addItemTrackingNumber)

  const { loading: updateItemRequestTrackingDetailsLoading } = useAppSelector(
    (state: RootState) => state.updateItemRequestTrackingDetails
  )

  const [isDesktop] = useResponsiveness()

  const dispatch = useAppDispatch()

  const handleGetItemRequestTrackingDetails = useCallback(async () => {
    setTrackingDetailsLoading(true)

    try {
      const payload: GetItemRequestTrackingDetailsRequestPayload = {
        request: {
          itemRequestId: itemRequestId.toString()
        }
      }

      const response = await dispatch(getItemRequestTrackingDetails(payload)).unwrap()
      setTrackingDetailsData(response)
    } catch (error) {
      // Error handling - could be improved with proper error state
    } finally {
      setTrackingDetailsLoading(false)
    }
  }, [dispatch, itemRequestId])

  // Load tracking details when component opens
  useEffect(() => {
    if (isOpen) {
      const loadTrackingDetails = async () => {
        setTrackingDetailsLoading(true)

        try {
          const payload: GetItemRequestTrackingDetailsRequestPayload = {
            request: {
              itemRequestId: itemRequestId.toString()
            }
          }

          const response = await dispatch(getItemRequestTrackingDetails(payload)).unwrap()
          setTrackingDetailsData(response)
        } catch (error) {
          // Error handling - could be improved with proper error state
        } finally {
          setTrackingDetailsLoading(false)
        }
      }

      loadTrackingDetails()
    }
  }, [isOpen, dispatch, itemRequestId]) // Removed trackingDetailsData from dependencies

  useEffect(() => {
    const trackingDetails = trackingDetailsData?.data.trackingDetails
    const hasLocker = !!trackingDetailsData?.data.lockerOrderDetails

    if (trackingDetails) {
      const statuses = getStatuses(trackingDetails, hasLocker)
      const visibleStatuses = statuses.filter((status) => status.visible)
      const currentStatus = getCurrentTrackingDetailsStatus(visibleStatuses)
      const currentStatusIndex = visibleStatuses.findIndex((status) => status.id === currentStatus)

      const isConfirmedDeliveredByShopper = statuses.some(
        (status) =>
          (status.id === TrackingDetailsKeys.ConfirmedDeliveredToShopperByShopper ||
            status.id === TrackingDetailsKeys.ConfirmedDeliveredToShopperViaLocker) &&
          status.status === true
      )

      const isConfirmedDeliveredByTraveler = statuses.some(
        (status) =>
          (status.id === TrackingDetailsKeys.ConfirmedDeliveredToShopperByTraveler ||
            status.id === TrackingDetailsKeys.ConfirmedDeliveredToShopperViaLocker) &&
          status.status === true
      )

      setVisibleStatuses(visibleStatuses)
      setCurrentStatus(currentStatus)
      setCurrentStatusIndex(currentStatusIndex)
      setIsConfirmedDeliveredByShopper(isConfirmedDeliveredByShopper)
      setIsConfirmedDeliveredByTraveler(isConfirmedDeliveredByTraveler)

      // Last step is completed for shopper when delivery is confirmed by shopper
      // Last step is completed for traveler when delivery is confirmed by traveler OR via locker
      if (isConfirmedDeliveredByShopper || isConfirmedDeliveredByTraveler) {
        setIsLastStepCompleted(true)
      } else {
        setIsLastStepCompleted(false)
      }
    }
  }, [trackingDetailsData])

  const handleAddItemTrackingNumber = useCallback(
    (trackingNumber: string) => {
      const payload: AddItemTrackingNumberRequestPayload = {
        request: {
          itemRequestId: itemRequestId.toString(),
          trackingNumber: trackingNumber
        },

        onSuccess: () => {
          handleGetItemRequestTrackingDetails()
          setTrackingNumber('')
        }
      }

      dispatch(addItemTrackingNumber(payload))
    },
    [dispatch, handleGetItemRequestTrackingDetails, itemRequestId]
  )

  const handleUpdateTrackingStatus = useCallback(() => {
    if (currentStatus) {
      const isTraveler = getUserRole() === UserRole.traveler

      const isDeliveryConfirmationStatus =
        currentStatus === TrackingDetailsKeys.ConfirmedDeliveredToShopperByTraveler ||
        currentStatus === TrackingDetailsKeys.ConfirmedDeliveredToShopperByShopper

      const payload: UpdateItemRequestTrackingDetailsRequestPayload = {
        request: {
          itemRequestId: itemRequestId.toString(),
          status: status.deliveryStatus[currentStatus as keyof typeof status.deliveryStatus]
        },

        onSuccess: () => {
          handleGetItemRequestTrackingDetails()

          if (getUserRole() === UserRole.shopper) {
            refreshMainList?.()
          }
        }
      }

      if (isDeliveryConfirmationStatus) {
        const subtitle = isTraveler
          ? 'Are you sure you want to confirm delivery of this item to the shopper?'
          : 'Are you sure you want to confirm receipt of this item from the traveler?'

        Modal.show({
          title: 'Confirm Delivery',
          subtitle,
          primaryButton: {
            label: 'Confirm',
            onClick: () => {
              dispatch(updateItemRequestTrackingDetails(payload))
            }
          },
          secondaryButton: {
            label: 'Dismiss'
          }
        })
      } else {
        dispatch(updateItemRequestTrackingDetails(payload))
      }
    }
  }, [currentStatus, dispatch, handleGetItemRequestTrackingDetails, itemRequestId, refreshMainList])

  const renderLoader = () => {
    return (
      <Box sx={styles.loaderContainer}>
        <Skeleton variant="rounded" height={20} width="40%" />

        <Box sx={styles.skeletonStepperContainer}>
          <Box sx={styles.skeletonStepsContainer}>
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} variant="rounded" height={20} width="20%" />
            ))}
          </Box>

          <Skeleton variant="rounded" height={45} width="100%" sx={styles.skeletonButton} />
        </Box>
      </Box>
    )
  }

  const renderTrackingDetails = useCallback(() => {
    const trackingDetails = trackingDetailsData?.data.trackingDetails
    const steps = visibleStatuses.map((status) => ({ label: status.name, subLabel: status.subLabel }))
    const userRole = getUserRole()
    const hasLocker = !!trackingDetailsData?.data.lockerOrderDetails

    if (trackingDetails) {
      const getStepContent = () => {
        if (userRole === UserRole.traveler) {
          let buttonText = 'Confirm'
          let buttonDisabled = false

          // TODO: Put these in a grid so that they can go directly below the relevant step in the stepper
          switch (currentStatus) {
            case TrackingDetailsKeys.PurchasedByAdmin:
            case TrackingDetailsKeys.PurchasedByAgentAdmin:
            case TrackingDetailsKeys.ConfirmedDeliveredToTravelerAddressByAdmin:
            case TrackingDetailsKeys.ConfirmedDeliveredToTravelerAddressByAgentAdmin:
              return

            case TrackingDetailsKeys.PurchasedByTraveler:
              return (
                <Box sx={styles.trackingDetailsFormContainer}>
                  <TextField
                    placeholder="Enter Tracking Number"
                    value={trackingNumber}
                    onChange={(event) => setTrackingNumber(event.target.value)}
                    fullWidth
                    sx={styles.trackingDetailsFormInput}
                  />

                  <Button
                    variant="contained"
                    color="primary"
                    buttonType="primary"
                    text="Find Order"
                    onClick={() => handleAddItemTrackingNumber(trackingNumber)}
                    disabled={trackingNumber?.length < 1}
                    loading={addItemTrackingNumberLoading}
                    style={styles.trackingDetailsFormButton}
                  />
                </Box>
              )

            case TrackingDetailsKeys.ReadyForDropoffAtLocker:
              if (trackingDetailsData?.data.lockerOrderDetails) {
                return (
                  <Box sx={styles.trackingDetailsFormContainer}>
                    <TextField
                      label="Dropoff Code"
                      value={trackingDetailsData.data.lockerOrderDetails.dropoffCode}
                      fullWidth
                      sx={styles.trackingDetailsFormInput}
                      InputProps={{
                        readOnly: true
                      }}
                      InputLabelProps={{
                        shrink: true
                      }}
                    />
                  </Box>
                )
              }

              return null

            case TrackingDetailsKeys.ConfirmedDeliveredToShopperByTraveler:
              // For locker deliveries, show dropoff code only before item is delivered (pickup_pending)
              if (hasLocker) {
                if (trackingDetailsData?.data.lockerOrderDetails?.dropoffCode && !isConfirmedDeliveredByTraveler) {
                  return (
                    <Box sx={styles.trackingDetailsFormContainer}>
                      <TextField
                        label="Dropoff Code"
                        value={trackingDetailsData.data.lockerOrderDetails.dropoffCode}
                        fullWidth
                        sx={styles.trackingDetailsFormInput}
                        InputProps={{
                          readOnly: true
                        }}
                        InputLabelProps={{
                          shrink: true
                        }}
                      />
                    </Box>
                  )
                }

                return null
              }

              if (isConfirmedDeliveredByTraveler) {
                if (!isConfirmedDeliveredByShopper) {
                  buttonText = isDesktop ? 'Awaiting confirmation from shopper' : 'Awaiting shopper confirmation'
                  buttonDisabled = true
                } else {
                  buttonText = 'Item Delivered'
                  buttonDisabled = true
                }
              } else {
                buttonText = 'Confirm'
                buttonDisabled = false
              }

              return (
                <Box sx={styles.trackingStatusActionContainer}>
                  <Button
                    variant="contained"
                    color="primary"
                    loading={updateItemRequestTrackingDetailsLoading}
                    fullWidth
                    buttonType="primary"
                    text={buttonText}
                    disabled={buttonDisabled}
                    onClick={handleUpdateTrackingStatus}
                  />
                </Box>
              )

            default:
              return (
                <Box sx={styles.trackingStatusActionContainer}>
                  <Button
                    variant="contained"
                    color="primary"
                    loading={updateItemRequestTrackingDetailsLoading}
                    fullWidth
                    buttonType="primary"
                    text={buttonText}
                    onClick={handleUpdateTrackingStatus}
                  />
                </Box>
              )
          }
        } else {
          let buttonText = 'Confirm'
          let buttonDisabled = false

          switch (currentStatus) {
            case TrackingDetailsKeys.AwaitingShopperPickupFromLocker:
              return null

            case TrackingDetailsKeys.ConfirmedDeliveredToShopperByShopper:
              // For locker deliveries, show pickup code only (no confirm button - automatic confirmation)
              if (hasLocker) {
                if (!isConfirmedDeliveredByShopper && trackingDetailsData?.data.lockerOrderDetails?.pickupCode) {
                  return (
                    <Box sx={styles.trackingDetailsFormContainer}>
                      <TextField
                        label="Pickup Code"
                        value={trackingDetailsData.data.lockerOrderDetails.pickupCode}
                        fullWidth
                        sx={styles.trackingDetailsFormInput}
                        InputProps={{
                          readOnly: true
                        }}
                        InputLabelProps={{
                          shrink: true
                        }}
                      />
                    </Box>
                  )
                }

                return null
              }

              // For non-locker deliveries, show confirm button
              if (isConfirmedDeliveredByTraveler) {
                if (isConfirmedDeliveredByShopper) {
                  buttonText = 'Item Delivered'
                  buttonDisabled = true
                } else {
                  buttonText = 'Confirm Delivery'
                  buttonDisabled = false
                }
              } else {
                buttonText = 'Confirm Delivery'
                buttonDisabled = true
              }

              return (
                <Box sx={styles.trackingStatusActionContainer}>
                  <Button
                    variant="contained"
                    color="primary"
                    loading={updateItemRequestTrackingDetailsLoading}
                    fullWidth
                    buttonType="primary"
                    text={buttonText}
                    disabled={buttonDisabled}
                    onClick={handleUpdateTrackingStatus}
                    style={styles.trackingDetailsFormButton}
                    tooltipText={!isConfirmedDeliveredByTraveler ? 'Awaiting confirmation from traveler' : undefined}
                  />
                </Box>
              )

            default:
              return null
          }
        }
      }

      return (
        <Box sx={styles.trackingDetailsContainer}>
          <Typography sx={styles.trackingDetailsTitle}>
            {userRole === UserRole.traveler
              ? 'Update the tracker below to inform the shopper'
              : 'View status updates for this item'}
          </Typography>

          <Box sx={styles.trackingDetailsStepperContainer(!!getStepContent())}>
            <Stepper
              activeStep={currentStatusIndex}
              steps={steps}
              noBackground
              isLastStepCompleted={isLastStepCompleted}
              orientation={isDesktop ? 'horizontal' : 'vertical'}
            >
              {getStepContent()}
            </Stepper>
          </Box>
        </Box>
      )
    }
  }, [
    trackingDetailsData?.data.trackingDetails,
    trackingDetailsData?.data.lockerOrderDetails,
    visibleStatuses,
    currentStatusIndex,
    isLastStepCompleted,
    currentStatus,
    trackingNumber,
    addItemTrackingNumberLoading,
    handleUpdateTrackingStatus,
    updateItemRequestTrackingDetailsLoading,
    handleAddItemTrackingNumber,
    isConfirmedDeliveredByShopper,
    isConfirmedDeliveredByTraveler,
    isDesktop
  ])

  const renderError = () => {
    return (
      <Box sx={styles.errorContainer}>
        <EmptyState
          title="Error"
          subtitle="There was an error loading the tracking details."
          button={{
            text: 'Try again',
            onClick: () => {
              handleGetItemRequestTrackingDetails()
            }
          }}
        />
      </Box>
    )
  }

  const renderContent = () => {
    let content = null

    if (trackingDetailsLoading) {
      content = renderLoader()
    } else if (visibleStatuses.length > 0) {
      content = renderTrackingDetails()
    } else {
      content = renderError()
    }

    return <Box sx={styles.contentContainer}>{content}</Box>
  }

  if (isOpen) {
    return (
      <Box sx={styles.container}>
        <Divider sx={styles.divider} />
        {renderContent()}
      </Box>
    )
  } else {
    return null
  }
}

export default TrackingDetails
